import { ref, computed, watch, ComponentPublicInstance } from "vue";
import gsap, { Power0, Power2, Power4, TimelineMax } from "gsap";
import * as ScrollMagic from "scrollmagic";
import axios from "axios";
import Debug from "debug";
import { StartEventParameters } from "scrollmagic";
import { SessionProgressApiModel, SessionsApiModel } from "../api-models";
import {
  SessionAnimationController,
  SessionLayoutType,
} from "../components/sessions/session-animation-controller";
import { Analytics } from "../services/analytics/analytics";

import {
  createSessionsViewModelFromApiModel,
  SessionsViewModel,
} from "../components/sessions/session-models";
import { csContext } from "../services/cs-context";

type SlideFinder =
  | Element
  | ComponentPublicInstance
  | Element[]
  | ComponentPublicInstance[]
  | Array<Element | Element[] | ComponentPublicInstance | ComponentPublicInstance[]>;

function isComponentInstance(value: any): value is ComponentPublicInstance {
  return value && typeof value === "object" && "$data" in value;
}

const debug = Debug("cs.SessionBase");
const sessionInlineBreakpointWidth = 1024; // Has an equivalent in session-variables.less: @session-inline-breakpoint-width

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function useSessionBase(getSessionId: () => string, instance: ComponentPublicInstance) {
  debug("useSessionBase");
  const titleTimescale = 1;
  const initialProgress = ref<number>();
  const latestProgressFromServer = ref<number>();
  const isLoading = ref(true);
  const isDestroyed = ref(false);
  const hasCompletedSession = ref(false);
  const layoutType = ref<SessionLayoutType>(SessionLayoutType.Wide);
  const sceneNames = ref<string[]>([]);
  const animations = ref<SessionAnimationController>();
  const storeProgressTimeout = ref<any>(null);

  const loadAnimations = ref<() => void>();

  const isInline = computed(() => {
    return layoutType.value === SessionLayoutType.Inline;
  });

  const titleVisualEase = computed(() => {
    return isInline.value ? Power0.easeNone : Power2.easeOut;
  });

  const titleTextEase = computed(() => {
    return isInline.value ? Power4.easeOut : Power4.easeOut;
  });

  function isLoadedChanged(newValue: boolean) {
    if (newValue) {
      document.body.classList.add("session-loading");
    } else {
      document.body.classList.remove("session-loading");
    }
  }

  watch(isLoading, (newValue) => {
    isLoadedChanged(newValue);
  });

  function initialize(sessionsModel?: SessionsApiModel) {
    debug("initialize");
    isLoadedChanged(true);
    if (sessionsModel) {
      const sessionsViewModel: SessionsViewModel =
        createSessionsViewModelFromApiModel(sessionsModel);
      const thisSession = sessionsViewModel.sessions.find((s) => s.id === getSessionId());
      const thisSessionProgress = sessionsModel.sessions.find((s) => s.id === getSessionId());
      hasCompletedSession.value = thisSession ? thisSession.isCompleted : false;
      latestProgressFromServer.value = thisSessionProgress
        ? thisSessionProgress.latestSceneIndex
        : undefined;
    }

    // Popover initialization
    $(function () {
      ($(".showPopover") as any).popover().click(function (e) {
        e.preventDefault();
      }); // Click preventDefault is for IE8 and 9.
    });
  }

  function refElements(refs?: SlideFinder): Element[] {
    if (!refs) {
      throw new Error("refs cannot be undefined or null");
    }

    let array: Element[] | ComponentPublicInstance[];
    const sessionElements: Element[] = [];

    if (isComponentInstance(refs)) {
      array = [refs as ComponentPublicInstance];
    } else if (refs instanceof Element) {
      array = [refs as Element];
    } else {
      array = refs as typeof array;
    }

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < array.length; i++) {
      if (isComponentInstance(array[i])) {
        sessionElements[i] = (array[i] as ComponentPublicInstance).$el;
      } else if (array[i] instanceof Element) {
        sessionElements[i] = array[i] as Element;
      }
    }

    return sessionElements;
  }

  function elements(explicitSlideId: SlideFinder | string | undefined): Element[] {
    let slideId = explicitSlideId;
    if (!slideId) {
      throw new Error("slideId must be provided");
    }

    let sessionElements: ComponentPublicInstance[] | Element[];

    if (typeof slideId === "string") {
      slideId = slideId.replace("#", "");

      // TODO: need to pass refs into this function as well
      sessionElements = refElements([
        (document.getElementById(slideId) as Element) || instance.$refs[slideId],
      ]);
    } else {
      sessionElements = refElements(slideId);
    }

    return sessionElements;
  }

  function zoomEnter(tl: TimelineMax, ele: SlideFinder | string): void {
    tl.fromTo(
      elements(ele),
      1,
      { scale: 0.8, autoAlpha: 0 },
      { autoAlpha: 1, ease: titleTextEase.value, scale: 1 },
    );
  }

  function zoomLeave(tl: TimelineMax, ele: SlideFinder | string): void {
    tl.to(elements(ele), 1, {
      autoAlpha: 0,
      ease: titleTextEase.value,
      scale: 0.8,
    });
  }

  function classScene(
    trigger: HTMLElement | string,
    target: HTMLElement | string,
    className: string,
    explicitTriggerPosition?: number | "title" | "default" | undefined,
  ): void {
    let triggerPosition = explicitTriggerPosition;
    if (triggerPosition === undefined || triggerPosition === "default") {
      triggerPosition = isInline.value ? 1 : 0.3;
    }

    if (triggerPosition === "title") {
      triggerPosition = isInline.value ? 0.98 : 1;
    }

    new ScrollMagic.Scene({
      triggerElement: trigger,
      triggerHook: triggerPosition,
    } as ScrollMagic.SceneOptions)
      .setClassToggle(target, className)
      .addTo(animations.value!.scrollController);
  }

  function getLayoutTypeToUse(): SessionLayoutType {
    return window.innerWidth < sessionInlineBreakpointWidth
      ? SessionLayoutType.Inline
      : SessionLayoutType.Wide;
  }

  function storeLatestSessionProgress(latestSceneIndex: number, forceWhileLoading = false): void {
    if (!isLoading.value || forceWhileLoading) {
      // Simple debounce to avoid hammering the server, particularly when loading/auto-scrolling
      if (storeProgressTimeout.value) {
        clearTimeout(storeProgressTimeout.value);
      }
      storeProgressTimeout.value = setTimeout(() => {
        debug("Storing latestSceneIndex", latestSceneIndex);
        axios
          .post("/api/sessions", {
            userId: csContext.user.id,
            sessionId: getSessionId(),
            latestSceneIndex,
          } as SessionProgressApiModel)
          .catch((e) => {
            // Do nothing - this is not a show-stopper
            // alert("Failed to update progress for session");
          });
      }, 500);
    }
  }

  // This is added just as a work around to get the types to play nicely below
  interface SessionWrapper extends ComponentPublicInstance {
    onProgress(newSceneIndex: number): void;
    latestProgress: number;
  }

  function handleSessionProgress(newSceneIndex: number): void {
    if (!instance) {
      throw new Error("Could not get instance");
    }

    if (typeof (instance.$parent as SessionWrapper).onProgress === "function") {
      (instance.$parent as SessionWrapper).onProgress(newSceneIndex);
    }

    storeLatestSessionProgress(newSceneIndex);
  }

  function onSessionProgress(newSceneIndex: number): void {
    handleSessionProgress(newSceneIndex);
  }

  function initializeAnimations(): void {
    debug("initializeAnimations");
    layoutType.value = getLayoutTypeToUse();
    animations.value = new SessionAnimationController(
      layoutType.value,
      (document.scrollingElement || document.documentElement) as HTMLElement, // Session-body-scrolling doesn't work 100%
      (newSceneIndex: number) => {
        onSessionProgress(newSceneIndex); // tracks and debug logs current slide
      },
    );
  }

  function element(slideId: SlideFinder | string): HTMLElement {
    const sessionElements = elements(slideId);

    if (!sessionElements[0]) {
      throw new Error(`Could not find element: ${slideId}`);
    }

    if (sessionElements[1]) {
      throw new Error(`More than one element with id or ref '${slideId}'`);
    }

    return sessionElements[0] as HTMLElement;
  }

  function sceneName(sessionElement: Element): string {
    if (!instance) {
      throw new Error("Could not get instance");
    }

    const refKeys = Object.keys(instance.$refs);

    let elementName = "";

    refKeys.forEach((key) => {
      if (instance.$refs[key] === sessionElement) {
        elementName = key;
      } else if ((instance.$refs[key] as ComponentPublicInstance).$el === sessionElement) {
        elementName = key;
      }
    });

    elementName = sessionElement.id || "unknown";
    const sessionSceneName = `${elementName}-scene`;

    if (sceneNames.value.includes(sessionSceneName)) {
      throw new Error(`Already included a scene for '${elementName}'`);
    }

    sceneNames.value.push(sessionSceneName);

    return sessionSceneName;
  }

  function scene(
    slideId: SlideFinder | string,
    explicitAnimationFunction?: (tl: TimelineMax) => void,
    explicitTriggerPosition?: "title" | "default" | number | undefined,
    explicitElementToScrollTo?: HTMLElement,
    onSceneCallBack?: (event: StartEventParameters) => void,
  ): void {
    const triggerElement = element(slideId);
    const animationFunction = explicitAnimationFunction || ((tl) => tl.to({}, 0.1, {}));

    let triggerPosition = explicitTriggerPosition;
    if (triggerPosition === undefined || triggerPosition === "default") {
      triggerPosition = isInline.value ? 1 : 0.3;
    }

    if (triggerPosition === "title") {
      triggerPosition = isInline.value ? 0.98 : 1;
    }

    if (!(triggerElement instanceof HTMLElement)) {
      throw new Error("SVG trigger elements not supported by scrollmagic");
    }

    const sessionScene = new ScrollMagic.Scene({
      triggerElement: triggerElement as HTMLElement,
      triggerHook: triggerPosition,
    });

    animations.value!.linkAnimationToScene(
      sceneName(triggerElement),
      () => {
        const tl = new TimelineMax({
          paused: true,
        });
        animationFunction!(tl);
        return tl;
      },
      sessionScene,
      undefined,
      explicitElementToScrollTo,
      onSceneCallBack,
    );
  }

  // eslint-disable-next-line no-undef
  function onWheel(event: WheelEvent): void {
    let delta = 0;
    if ((event as any).wheelDelta) {
      delta = (event as any).wheelDelta / 120;
    } else if (event.detail) {
      delta = -event.detail / 3;
    }

    animations.value!.handleWheelScroll(delta);
    if (event.preventDefault) {
      event.preventDefault();
    }
    event.returnValue = false;
  }

  const imagesHaveBeenLoaded = ref(false);

  function initializeAfterImagesLoaded(): void {
    debug("initializeAfterImagesLoaded");

    if (imagesHaveBeenLoaded.value) {
      // This is a workaround for v-team-effectiveness-loaded being called twice in Session 1.
      // This may be a bug, or it may be happening because
      // The strength mapping is being loaded after the rest of
      // The session, and has it's own team-effectiveness.
      // Nonetheless we skip the call to fix the issue.
      debug("onImagesLoaded called before, skipping second call.");
      return;
    }
    imagesHaveBeenLoaded.value = true;

    if (isDestroyed.value) {
      debug("Cancelling load for destroyed component");
      return;
    }
    // Always start by scrolling to the top, to prevent restoring
    // of the scroll position after a soft reload from messing things up
    animations.value!.scrollToTop().then(() => {
      if (loadAnimations.value) {
        loadAnimations.value();
      }

      if (!instance) {
        throw new Error("Could not get instance");
      }

      initialProgress.value =
        (instance.$parent as SessionWrapper).latestProgress ||
        latestProgressFromServer.value ||
        undefined;

      if (initialProgress.value && initialProgress.value >= 0) {
        debug("Initializing on scene", initialProgress);
        animations.value!.goStraightToScene(initialProgress.value as number).then(() => {
          isLoading.value = false;
        });
      } else {
        debug("Initializing at start", initialProgress.value);
        animations.value!.scrollToInitialPosition();
        isLoading.value = false;
      }

      animations.value!.scrollController.update();
      // The above update is only necessary for Chrome devices below 540px wide.
      // I don't know why exactly the problem occurs
      // and i don't understand how this fixes it,
      // But it does indeed fix the problem of triggers
      // being in the wrong position on chrome.

      if (window.location.port === "8080") {
        // We have to add indicators after the update or
        // They'll be in wrong position on chrome sometimes.
        // this.animations.addIndicators(); // Addindicators is commented out because it's causing bugs.
      }
    }); // Setting this timeout lower causes an issue where
    // the animations, like the grey background which compels from the title visuals
    // don't play from load-in.
    // Would be better to investigate the precise cause but don't have time.
  }

  // TODO: make sure this is always explicitly called
  function destroyed() {
    isDestroyed.value = true;
    if (animations.value) {
      animations.value.destroy();
    }
  }

  function getSessionVersion(): string {
    return `${getSessionId()}-first-base`;
  }

  function handleCloseSession(): void {
    isLoading.value = true;
    Analytics.action("Closed Session", "SessionActions", {
      sessionName: getSessionId(),
      sessionVersion: getSessionVersion(),
    });

    storeLatestSessionProgress(0, true); // Reset progress
    setTimeout(() => {
      // TODO: check this works
      window.location.href =
        (instance as ComponentPublicInstance & { closeUrl: string }).closeUrl ?? "/home";
    }, 2000);
  }

  function handleKeyDown(event: KeyboardEvent): void {
    debug("onKeyDown");
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.stopImmediatePropagation();
      event.preventDefault();
      animations.value!.nextSlide();
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.stopImmediatePropagation();
      event.preventDefault();
      animations.value!.previousSlide();
    }
  }

  return {
    titleTimescale,
    initialProgress,
    latestProgressFromServer,
    isLoading,
    isDestroyed,
    hasCompletedSession,
    layoutType,
    sceneNames,
    animations,
    storeProgressTimeout,
    isInline,
    titleVisualEase,
    titleTextEase,
    isLoadedChanged,
    initialize,
    refElements,
    elements,
    zoomEnter,
    zoomLeave,
    classScene,
    getLayoutTypeToUse,
    storeLatestSessionProgress,
    handleSessionProgress,
    onSessionProgress,
    initializeAnimations,
    element,
    sceneName,
    scene,
    onWheel,
    imagesHaveBeenLoaded,
    initializeAfterImagesLoaded,
    getSessionVersion,
    handleCloseSession,
    handleKeyDown,
    loadAnimations,
    destroyed,
  };
}

export default useSessionBase;
